import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Briefcase, Loader2, Pencil, Plus, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { ConfirmDialog, EmptyState, Field, PageHeader, PaginationBar, StatusBadge } from "@/components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const CASE_STATUSES = ["New", "Active", "Pending", "Won", "Lost", "Closed"];
const AREAS = ["Corporate Law", "Civil Litigation", "Family Law", "Real Estate", "Criminal Defense", "Estate Planning"];
const inputCls = "bg-white border border-border focus:border-gold px-3 py-2.5 w-full outline-none transition text-sm rounded-none";

const EMPTY_FORM = { client_id: "", title: "", practice_area: "", status: "New", filed_date: "", next_hearing: "", description: "", notes: "" };

export default function Cases() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [formOpen, setFormOpen] = useState(params.get("new") === "1");
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/clients?limit=100").then(({ data }) => setClients(data.items)).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(() => {
    const q = new URLSearchParams({ page, limit: 10 });
    if (debounced) q.set("search", debounced);
    if (status) q.set("status", status);
    api.get(`/cases?${q}`).then(({ data }) => setData(data)).catch((e) => toast.error(errMsg(e)));
  }, [page, debounced, status]);

  useEffect(() => { load(); }, [load]);

  const openForm = (c) => {
    setEditing(c);
    setForm(c ? { ...EMPTY_FORM, ...c } : EMPTY_FORM);
    setErrors({});
    setFormOpen(true);
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.title.trim()) errs.title = "Case title is required";
    if (!form.client_id) errs.client_id = "Select a client";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setSaving(true);
    try {
      if (editing) await api.put(`/cases/${editing.id}`, form);
      else await api.post("/cases", form);
      toast.success(editing ? "Case updated" : "Case opened");
      setFormOpen(false);
      setEditing(null);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/cases/${deleting.id}`);
      toast.success(`Case ${deleting.case_number} deleted`);
      setDeleting(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const statusCounts = CASE_STATUSES.map((s) => ({
    status: s,
    count: status === s ? data?.total ?? 0 : null,
  }));

  return (
    <div data-testid="cases-page">
      <PageHeader title="Cases" description="Track every matter from intake to verdict." testId="cases-header">
        <Button onClick={() => openForm(null)} data-testid="add-case-btn"
          className="bg-navy text-ivory hover:bg-navy-800 rounded-none uppercase tracking-wider text-xs font-semibold">
          <Plus className="h-4 w-4 mr-1.5" /> Open Case
        </Button>
      </PageHeader>

      <div className="flex flex-wrap gap-2 mb-6" data-testid="case-status-filters">
        <button onClick={() => { setStatus(""); setPage(1); }} data-testid="case-filter-all"
          className={`px-4 py-2 text-xs font-semibold uppercase tracking-wider border transition-colors ${!status ? "bg-navy text-ivory border-navy" : "border-border text-muted-foreground hover:border-navy hover:text-navy"}`}>
          All
        </button>
        {statusCounts.map(({ status: s }) => (
          <button key={s} onClick={() => { setStatus(s); setPage(1); }} data-testid={`case-filter-${s.toLowerCase()}`}
            className={`px-4 py-2 text-xs font-semibold uppercase tracking-wider border transition-colors ${status === s ? "bg-navy text-ivory border-navy" : "border-border text-muted-foreground hover:border-navy hover:text-navy"}`}>
            {s}
          </button>
        ))}
      </div>

      <div className="relative max-w-md mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <input className={`${inputCls} pl-10`} placeholder="Search by title, case number, or client…" value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }} data-testid="case-search-input" />
      </div>

      {!data ? (
        <div className="bg-white border border-border p-4 space-y-3" data-testid="cases-skeleton">
          {[...Array(6)].map((_, i) => <div key={i} className="h-14 bg-muted animate-pulse" />)}
        </div>
      ) : data.items.length === 0 ? (
        <EmptyState icon={Briefcase} title="No cases found"
          description={debounced || status ? "Adjust your search or status filter." : "Open your first case to begin tracking a matter."}
          testId="cases-empty"
          action={<Button onClick={() => openForm(null)} className="bg-navy text-ivory rounded-none" data-testid="empty-add-case"><Plus className="h-4 w-4 mr-1.5" /> Open Case</Button>} />
      ) : (
        <>
          <div className="bg-white border border-border shadow-card overflow-x-auto">
            <table className="w-full text-sm min-w-[860px]" data-testid="cases-table">
              <thead>
                <tr className="border-b border-border text-left">
                  {["Case", "Client", "Practice Area", "Status", "Filed", "Next Hearing", ""].map((h) => (
                    <th key={h} className="px-6 py-4 text-xs font-semibold uppercase tracking-widest text-muted-foreground">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {data.items.map((c) => (
                  <tr key={c.id} className="hover:bg-muted/50 transition-colors cursor-pointer" onClick={() => openForm(c)} data-testid={`case-row-${c.id}`}>
                    <td className="px-6 py-4">
                      <p className="font-semibold text-navy">{c.title}</p>
                      <p className="text-xs text-muted-foreground font-mono mt-0.5">{c.case_number}</p>
                    </td>
                    <td className="px-6 py-4 text-foreground">{c.client_name}</td>
                    <td className="px-6 py-4 text-muted-foreground">{c.practice_area || "—"}</td>
                    <td className="px-6 py-4"><StatusBadge status={c.status} /></td>
                    <td className="px-6 py-4 text-muted-foreground">{fmtDate(c.filed_date)}</td>
                    <td className="px-6 py-4">
                      {c.next_hearing ? <span className="text-gold-700 font-semibold text-xs uppercase tracking-wider">{fmtDate(c.next_hearing)}</span> : <span className="text-muted-foreground">—</span>}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex gap-1 justify-end" onClick={(e) => e.stopPropagation()}>
                        <button className="p-2 text-muted-foreground hover:text-navy hover:bg-muted transition-colors" data-testid={`edit-case-${c.id}`} onClick={() => openForm(c)} aria-label="Edit">
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button className="p-2 text-muted-foreground hover:text-red-600 hover:bg-red-50 transition-colors" data-testid={`delete-case-${c.id}`} onClick={() => setDeleting(c)} aria-label="Delete">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <PaginationBar page={data.page} pages={data.pages} total={data.total} onChange={setPage} />
        </>
      )}

      <Dialog open={formOpen} onOpenChange={(v) => { setFormOpen(v); if (!v) { setEditing(null); params.delete("new"); setParams(params, { replace: true }); } }}>
        <DialogContent className="max-w-2xl" data-testid="case-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-navy">
              {editing ? `Edit Case ${editing.case_number}` : "Open a New Case"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="grid sm:grid-cols-2 gap-5 mt-2" noValidate>
            <div className="sm:col-span-2">
              <Field label="Case Title *" error={errors.title} htmlFor="case-title">
                <input id="case-title" className={inputCls} value={form.title} onChange={set("title")} data-testid="case-title-input" placeholder="e.g. Bennett v. Sterling Logistics" />
              </Field>
            </div>
            <Field label="Client *" error={errors.client_id} htmlFor="case-client">
              <select id="case-client" className={inputCls} value={form.client_id} onChange={set("client_id")} data-testid="case-client-select">
                <option value="">Select client…</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </Field>
            <Field label="Practice Area" htmlFor="case-area">
              <select id="case-area" className={inputCls} value={form.practice_area} onChange={set("practice_area")} data-testid="case-area-select">
                <option value="">Select…</option>
                {AREAS.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </Field>
            <Field label="Status" htmlFor="case-status">
              <select id="case-status" className={inputCls} value={form.status} onChange={set("status")} data-testid="case-status-select">
                {CASE_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="Filed Date" htmlFor="case-filed">
              <input id="case-filed" type="date" className={inputCls} value={form.filed_date || ""} onChange={set("filed_date")} data-testid="case-filed-input" />
            </Field>
            <Field label="Next Hearing" htmlFor="case-hearing">
              <input id="case-hearing" type="date" className={inputCls} value={form.next_hearing || ""} onChange={set("next_hearing")} data-testid="case-hearing-input" />
            </Field>
            <div className="sm:col-span-2">
              <Field label="Description" htmlFor="case-description">
                <textarea id="case-description" rows={3} className={`${inputCls} resize-none`} value={form.description || ""} onChange={set("description")} data-testid="case-description-input" placeholder="Nature of the matter, claims, posture…" />
              </Field>
            </div>
            <div className="sm:col-span-2">
              <Field label="Internal Notes" htmlFor="case-notes">
                <textarea id="case-notes" rows={2} className={`${inputCls} resize-none`} value={form.notes || ""} onChange={set("notes")} data-testid="case-notes-input" placeholder="Strategy, reminders, key deadlines…" />
              </Field>
            </div>
            <div className="sm:col-span-2 flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setFormOpen(false)} data-testid="case-form-cancel">Cancel</Button>
              <Button type="submit" disabled={saving} data-testid="case-form-submit" className="bg-navy text-ivory hover:bg-navy-800 rounded-none">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? "Save Changes" : "Open Case"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={!!deleting} onOpenChange={() => setDeleting(null)}
        title="Delete this case?"
        description={`Case ${deleting?.case_number} — "${deleting?.title}" — will be permanently removed. Documents remain in the vault.`}
        confirmLabel="Delete Case" onConfirm={doDelete} />
    </div>
  );
}
